import { useEffect } from 'react';
import { cdnUrl } from '@/shared/config';
import { logger } from '@/shared/utils/logger';

/**
 * Hook to preload images in the background.
 * Preloads URLs as-is — no format negotiation.
 * See: docs/features/IMAGE_DELIVERY_STRATEGY_2026-03-17.md
 */
export const useImagePreload = (imageUrls: string[], enabled = true) => {
  useEffect(() => {
    if (!enabled || imageUrls.length === 0) return;

    const preloadImages = async () => {
      const promises = imageUrls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
      });

      try {
        await Promise.allSettled(promises);
      } catch (error) {
        // Silently fail - preloading is an optimization
        logger.debug('Image preload failed for some images:', error);
      }
    };

    // Delay preloading to avoid blocking initial render
    const timer = setTimeout(preloadImages, 1000);

    return () => clearTimeout(timer);
  }, [imageUrls, enabled]);
};

/**
 * Preload common service images that are frequently used.
 * Uses the current contract path: webp/desktop.
 */
export const usePreloadCommonServiceImages = () => {
  const base = `${cdnUrl}/services/hairstyles/webp/desktop`;
  const commonImages = [
    `${base}/bald.webp`,
    `${base}/beardTrim.webp`,
    `${base}/coily-taper.webp`,
    `${base}/straight_taper.webp`,
    `${base}/high_fade.webp`,
    `${base}/lineup.webp`,
  ];

  useImagePreload(commonImages);
};
